// The "Remove-confirm" connection-state card (§s03, execution plan §6.1
// "deliberately NOT new kinds" -- a transient dialog stage, not a route
// state). States the real consequence: DELETE /api/plaid/items/{id} purges
// the token at Plaid; transactions stay, labelled as of the last sync. The
// transaction count comes from the API (via `transactionCount`) or is
// omitted entirely -- never invented (WS-3 step 5).
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button } from '@moneyskyline/ui';

export function RemoveConnectionDialog({
  open,
  institutionName,
  transactionCount,
  lastSyncedAt,
  onConfirm,
  onOpenChange,
  removing,
}: {
  open: boolean;
  institutionName: string | null;
  /** From the API only; omit the sentence entirely rather than guess. */
  transactionCount?: number | null;
  lastSyncedAt?: string | null;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  removing?: boolean;
}) {
  return (
    <RemoveConnectionDialogInstance
      key={open ? 'open' : 'closed'}
      open={open}
      institutionName={institutionName}
      transactionCount={transactionCount}
      lastSyncedAt={lastSyncedAt}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
      removing={removing}
    />
  );
}

function RemoveConnectionDialogInstance({
  open,
  institutionName,
  transactionCount,
  lastSyncedAt,
  onConfirm,
  onOpenChange,
  removing,
}: {
  open: boolean;
  institutionName: string | null;
  transactionCount?: number | null;
  lastSyncedAt?: string | null;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  removing?: boolean;
}) {
  const [confirmed, setConfirmed] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="remove-connection-dialog">
        <DialogHeader>
          <DialogTitle>Remove {institutionName ?? 'this connection'}?</DialogTitle>
          <DialogDescription>
            Stops syncing and deletes the token at Plaid.
            {typeof transactionCount === 'number'
              ? ` ${transactionCount.toLocaleString()} transactions stay`
              : ' Existing transactions stay'}
            {lastSyncedAt ? `, labelled as of ${lastSyncedAt}.` : '.'}
            {' Removing a connection does not restore the Plaid trial Item allowance.'}
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            disabled={removing}
          />
          <span>I understand this permanently removes the Plaid connection and may consume another trial Item if I add it again.</span>
        </label>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={removing}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={removing || !confirmed}>
            {removing ? 'Removing…' : 'Remove connection'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RemoveConnectionDialog;
